/**
 * PastDueBanner — Sticky app-wide banner shown above all authenticated screens
 * while the user's subscription is `past_due` or `suspended` (Phase 8 / SOS-335).
 *
 * Auto-hides on:
 *   - non-authenticated routes
 *   - subscription states that are not past_due / suspended
 *   - the `/settings/subscription` route itself (the page already shows the banner inline)
 *
 * Session-level dismiss (Spec rule 3): the user can dismiss for the current
 * session via the X button. Refreshing the app brings it back. We persist
 * dismissal in `sessionStorage` (cleared on tab close) to keep this scoped.
 *
 * Reuses SubscriptionAlertBanner for styling. The banner positions itself
 * `sticky top-0` inside its slot so it floats above the page content.
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { Button } from '@/components/ui/button';

const DISMISS_STORAGE_KEY = 'sossiety:past_due_banner_dismissed';
// Routes where the banner is intentionally suppressed.
const SUPPRESSED_PATHS = [
  '/settings/subscription',
  '/settings/subscription/cancel',
  '/subscription',
  '/subscription/checkout',
  '/subscription/success',
];

export const PastDueBanner: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { displayState, isLoading } = useSubscriptionStatus();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  });

  // Re-evaluate dismissal when subscription state changes (e.g. recovery → clear).
  useEffect(() => {
    if (displayState === 'active' || displayState === 'none' || displayState === 'expired') {
      window.sessionStorage.removeItem(DISMISS_STORAGE_KEY);
      setDismissed(false);
    }
  }, [displayState]);

  if (isLoading) return null;
  if (displayState !== 'past_due' && displayState !== 'suspended') return null;
  if (dismissed) return null;
  if (SUPPRESSED_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  const isSuspended = displayState === 'suspended';
  const onDismiss = () => {
    window.sessionStorage.setItem(DISMISS_STORAGE_KEY, '1');
    setDismissed(true);
  };

  return (
    <div data-testid="past-due-banner" className="sticky top-0 z-40 px-3 pt-3 pointer-events-none">
      <div
        className="rounded-xl border bg-[#D72E16]/15 border-[#D72E16]/50 backdrop-blur-md p-3 flex items-start gap-3 shadow-lg pointer-events-auto"
        role="alert"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">
            {isSuspended ? t('premium.dunning.suspended_title') : t('premium.dunning.past_due_title')}
          </p>
          <p className="text-xs text-white/80 mt-0.5 leading-snug">
            {isSuspended ? t('premium.dunning.suspended_body') : t('premium.dunning.past_due_body')}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              data-testid="past-due-banner-update-cta"
              size="sm"
              variant="primary"
              className="h-8 px-3 text-xs"
              onClick={() => navigate('/settings/subscription')}
            >
              {t('premium.dunning.update_card_cta')}
            </Button>
          </div>
        </div>
        <button
          data-testid="past-due-banner-dismiss"
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 text-white/70 hover:text-white p-1"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};
